import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useMiniAppApi } from '../lib/context';
import { useAuthState } from '../lib/auth-context';
import { formatDate, formatTime, formatRelativeTime, getDateGroupKey } from '../lib/date-utils';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';

export default function TasksView() {
  const api = useMiniAppApi();
  const auth = useAuthState();
  const userIdentifier = auth.session?.userId ?? auth.session?.telegramUserId;
  const canQuery =
    !!userIdentifier &&
    (auth.status === 'authenticated' ||
      auth.status === 'cached-session' ||
      auth.status === 'read-only');
  const timezone = 'UTC'; // TODO: Get from settings when available

  const queryClient = useQueryClient();
  
  const tasksQuery = useQuery({
    queryKey: ['tasks', userIdentifier],
    queryFn: () => api.getTasks(userIdentifier || undefined),
    enabled: canQuery,
  });

  // Mutation for updating task status
  const updateTaskMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'pending' | 'done' }) => {
      return api.updateTask(id, { status: status === 'done' ? 'done' : 'pending' });
    },
    onSuccess: () => {
      // Invalidate and refetch tasks
      queryClient.invalidateQueries({ queryKey: ['tasks', userIdentifier] });
      // Also invalidate summary counts
      queryClient.invalidateQueries({ queryKey: ['tasks', userIdentifier, 'count'] });
    },
  });

  // Mutation for deleting task
  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => {
      return api.deleteTask(id);
    },
    onSuccess: () => {
      // Invalidate and refetch tasks
      queryClient.invalidateQueries({ queryKey: ['tasks', userIdentifier] });
      // Also invalidate summary counts
      queryClient.invalidateQueries({ queryKey: ['tasks', userIdentifier, 'count'] });
    },
  });

  // Group tasks by date and status
  const groupedTasks = useMemo(() => {
    if (!tasksQuery.data) return new Map<string, typeof tasksQuery.data>();
    
    const groups = new Map<string, typeof tasksQuery.data>();
    
    // Separate by status first
    const pending = tasksQuery.data.filter(t => t.status === 'pending');
    const done = tasksQuery.data.filter(t => t.status === 'done');
    
    // Group pending tasks by date
    const pendingByDate = new Map<string, typeof tasksQuery.data>();
    for (const task of pending) {
      const dateKey = task.due ? getDateGroupKey(task.due, timezone) : 'Без даты';
      if (!pendingByDate.has(dateKey)) {
        pendingByDate.set(dateKey, []);
      }
      pendingByDate.get(dateKey)!.push(task);
    }
    
    // Add pending groups
    for (const [dateKey, tasks] of pendingByDate.entries()) {
      groups.set(`pending_${dateKey}`, tasks);
    }
    
    // Add done tasks if any
    if (done.length > 0) {
      groups.set('done', done);
    }
    
    return groups;
  }, [tasksQuery.data, timezone]);

  if (tasksQuery.isLoading) {
    return (
      <div className="card">
        <h2>Задачи</h2>
        <LoadingSkeleton count={3} />
      </div>
    );
  }

  if (!userIdentifier) {
    return (
      <div className="card card--info">
        <h2>Задачи</h2>
        {auth.status === 'needs-initdata' && (
          <>
            <p>Нужно открыть мини-приложение через кнопку в боте, чтобы загрузить ваши задачи.</p>
            <p style={{ marginTop: '0.5rem', color: '#666' }}>
              Мы не получили данные Telegram. Нажмите «Проверить снова» или откройте приложение из чата с ботом.
            </p>
            <button onClick={auth.retry} style={{ marginTop: '1rem' }}>
              Проверить снова
            </button>
          </>
        )}
        {auth.status === 'read-only' && (
          <>
            <p>Вы просматриваете приложение в браузере. Для доступа к личным задачам запустите мини-приложение внутри Telegram.</p>
          </>
        )}
        {auth.status === 'error' && (
          <>
            <p>Не удалось авторизоваться. {auth.message ?? 'Попробуйте снова или откройте приложение через Telegram.'}</p>
            <button onClick={auth.retry} style={{ marginTop: '1rem' }}>
              Повторить попытку
            </button>
          </>
        )}
        {auth.status !== 'needs-initdata' &&
          auth.status !== 'read-only' &&
          auth.status !== 'error' && (
            <p>Для просмотра задач требуется авторизация через Telegram.</p>
          )}
      </div>
    );
  }

  if (tasksQuery.isError) {
    const error = tasksQuery.error as AxiosError | Error;
    let title = 'Не удалось загрузить задачи';
    let message =
      'Проверьте подключение к интернету и попробуйте снова. Если проблема сохраняется, используйте Telegram бота.';

    if ('isAxiosError' in (error as AxiosError) && (error as AxiosError).isAxiosError) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        title = 'Ошибка API';
        message = `Сервер ответил статусом ${axiosError.response.status}. Попробуйте позже или сообщите поддержке.`;
      } else {
        title = 'Ошибка сети';
        message = 'Не удалось связаться с сервером. Проверьте подключение к интернету и попробуйте снова.';
      }
    }

    return (
      <div className="card">
        <ErrorState title={title} message={message} onRetry={() => tasksQuery.refetch()} />
      </div>
    );
  }

  const totalTasks = tasksQuery.data?.length || 0;
  const pendingCount = tasksQuery.data?.filter(t => t.status === 'pending').length || 0;
  const doneCount = tasksQuery.data?.filter(t => t.status === 'done').length || 0;

  const isReadOnly = auth.status === 'read-only' || auth.status === 'needs-initdata';
  const isAuthenticated = auth.status === 'authenticated' || auth.status === 'cached-session';

  return (
    <div className="stack">
      {isReadOnly && (
        <div className="card card--info">
          <h2>Режим просмотра</h2>
          <p>Этот интерфейс работает только в режиме чтения. Для создания, редактирования и удаления задач используйте Telegram бота.</p>
        </div>
      )}

      {totalTasks > 0 && (
        <div className="card">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0' }}>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>Всего</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{totalTasks}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>Активных</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2196F3' }}>{pendingCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>Выполнено</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#4CAF50' }}>{doneCount}</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Задачи</h2>
        {totalTasks === 0 ? (
          <EmptyState
            icon="📋"
            title="У вас пока нет активных задач"
            message={isReadOnly 
              ? "Создайте задачу через Telegram бота, и она появится здесь. Этот интерфейс работает только в режиме просмотра."
              : "Создайте задачу через Telegram бота, и она появится здесь."}
          />
        ) : (
          <div className="list">
            {Array.from(groupedTasks.entries()).map(([groupKey, tasks]) => {
              const isDoneGroup = groupKey === 'done';
              const dateKey = groupKey.replace(/^(pending|done)_/, '');
              
              return (
                <div key={groupKey} style={{ marginBottom: '1.5rem' }}>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    color: '#666', 
                    marginBottom: '0.5rem',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    {isDoneGroup ? '✅ Выполнено' : dateKey}
                  </div>
                  {tasks.map(task => (
                    <div key={task.id} className="list__item" style={{ 
                      opacity: isDoneGroup ? 0.7 : 1,
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div className="list__title" style={{ 
                          textDecoration: isDoneGroup ? 'line-through' : 'none',
                          color: isDoneGroup ? '#999' : 'inherit'
                        }}>
                          {task.title}
                        </div>
                        {task.due && (
                          <div className="list__meta" style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            alignItems: 'center',
                            marginTop: '0.25rem'
                          }}>
                            <span>📅 {formatDate(task.due, timezone)}</span>
                            <span>🕐 {formatTime(task.due, timezone)}</span>
                            {!isDoneGroup && (
                              <span style={{ color: '#2196F3', fontSize: '0.875rem' }}>
                                ({formatRelativeTime(task.due, timezone)})
                              </span>
                            )}
                          </div>
                        )}
                        {timezone && timezone !== 'UTC' && (
                          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                            Часовой пояс: {timezone}
                          </div>
                        )}
                      </div>
                      <div className="list__actions">
                        <button 
                          disabled={!isAuthenticated || updateTaskMutation.isPending} 
                          onClick={() => {
                            const newStatus = task.status === 'done' ? 'pending' : 'done';
                            updateTaskMutation.mutate({ id: task.id, status: newStatus });
                          }}
                          title={!isAuthenticated ? "Требуется авторизация" : undefined}
                        >
                          {updateTaskMutation.isPending ? '...' : task.status === 'done' ? 'Вернуть' : 'Выполнено'}
                        </button>
                        <button 
                          className="danger" 
                          disabled={!isAuthenticated || deleteTaskMutation.isPending} 
                          onClick={() => {
                            if (confirm(`Удалить задачу "${task.title}"?`)) {
                              deleteTaskMutation.mutate(task.id);
                            }
                          }}
                          title={!isAuthenticated ? "Требуется авторизация" : undefined}
                        >
                          {deleteTaskMutation.isPending ? '...' : 'Удалить'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
